import { GetStaticProps } from "next";
import { useEffect } from "react";
import { ProductsListingLayout } from "@Components/products-listing/ProductsListingLayout";
import { ProductsFilterProvider } from "@Components/products-listing/context/ProductsFilterContext";
import { useProductsSearchCriteriaByURL } from "@Components/products-listing/hooks";
import { IProductsListingPageContent } from "@Components/products-listing/types";
import { DY_PAGE_CONTEXT } from "@Constants/common";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import CmsPage, { getCMSPageModel } from "@Libs/contentful/components/CmsPage";
import { RESOURCE_NAMES } from "@Libs/contentful/constants";
import ProductsListingPageQuery, {
  queryPLPPaths,
} from "@Libs/contentful/queries/ProductsListingPage";
import { setDYContext } from "@Libs/dynamic-yield/dynamicYield";
import { makeStaticProps } from "@Libs/i18n/getStatic";
import { getCtData } from "@Utils/HomePageHelpers";
import logger from "@Utils/Logger";
import { getItem } from "@Utils/browserStorage";
import { getStaticPathsFromSlug } from "@Utils/contentful";
import { useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";

const ProductListingPage = ({ pageModel }: any) => {
  const { pageContent = {} } = pageModel;
  const { languageCode } = useAppSelector(localeSelector);
  /**
   * There is no specific property as 'category' in the pageModel.
   * For now, assigned the pageTitle as Category name
   */
  const { pageTitle } = pageContent;
  const searchCriteria = useProductsSearchCriteriaByURL();
  const subCategory = getItem(
    STORAGE_KEYS.DY_PRODUCTS_SEARCH_CRITERIA,
    STORAGE_TYPES.SESSION
  );

  //Set 'CATEGORY' DY-PageContext
  useEffect(
    () =>
      setDYContext(
        DY_PAGE_CONTEXT.CATEGORY,
        languageCode,
        subCategory && Object.keys(searchCriteria).length > 0
          ? [pageTitle, subCategory]
          : [pageTitle]
      ),
    [pageTitle, subCategory, searchCriteria, languageCode]
  );

  return (
    <CmsPage
      pageModel={pageModel}
      pageTitle={pageContent.pageTitle}
      pageDescription={pageContent.pageDescription}
    >
      <ProductsFilterProvider>
        <ProductsListingLayout
          {...(pageContent as IProductsListingPageContent)}
        />
      </ProductsFilterProvider>
    </CmsPage>
  );
};

type Params = {
  slug: string;
  locale: string;
};

const overrideStaticProps: GetStaticProps = async (props) => {
  const { locale, slug } = props as Params;

  const pageModel = await getCMSPageModel(
    ProductsListingPageQuery,
    {
      locale,
      slug,
      resourceName: RESOURCE_NAMES.productListingPage,
    },
    {
      keepOriginalSlug: true,
    }
  );

  if (pageModel?.pageContent?.products?.items?.length) {
    for (const collection of pageModel?.pageContent.products.items) {
      if (collection?.ctproductSku) {
        collection.ctData = (await getCtData(collection.ctproductSku)) || null;
      }
    }
  }

  return {
    props: { pageModel },
  };
};

export async function getStaticPaths() {
  try {
    const paths = await getStaticPathsFromSlug(queryPLPPaths);

    return {
      paths,
      fallback: false,
    };
  } catch (error) {
    logger.error("collection/[slug] getStaticPaths()", error);
  }
}

const getStaticProps = makeStaticProps(["common"], overrideStaticProps);
export { getStaticProps };

export default ProductListingPage;
